import React, { useEffect, useRef } from "react";

import { getGroupShellTitle } from "@/components/business/FillInTableContainer/shared/utils/getTextFromData";
import { ExpeditedProgramSection } from "@/types/expeditedProgram.types";
import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import { useGetNumberOfShell } from "@FillInTableContainer/shared/hooks/useGetNumberOfShell";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { newArrayToCountValue } from "@utils/newArrayToCountValue";

import ExpeditedProgramSingleTypeShell from "./components/ExpeditedProgramSingleTypeShell/ExpeditedProgramSingleTypeShell";
import * as S from "./ExpeditedProgramSingleTypeGroupShell.style";
import { useExpeditedProgramGroupShellCreator } from "./hooks/useExpeditedProgramGroupShellCreator";

import type { TableCreatorShellList } from "@FillInTableContainer/shared/types/tableCreator.types";

interface Props {
  groupTableKey: ExpeditedProgramSection;
  groupTableIndex: number;
  groupShellKey: string | null;
  shellList: TableCreatorShellList;
  tableKey: string | null;
  tableIndex: number;
  groupShellIndex: number;
  groupShellCount: number;
}

const ExpeditedProgramSingleTypeGroupShell = ({
  groupTableKey,
  groupTableIndex,
  groupShellKey,
  shellList,
  tableKey,
  tableIndex,
  groupShellIndex,
  groupShellCount,
}: Props) => {
  const positionPropsInfo = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    tableKey,
    tableIndex,
  };
  const { shellCount } = useGetNumberOfShell();
  const articleSection = useArticleSection();
  const selectButtonRef = useRef<HTMLDivElement>(null);
  const {
    focusStatus,
    isFocused,
    isClearGroupShell,
    isDeleteGroupShell,
    isAlertGroupShell,
    isEnableEdit,
    handleDefaultFocus,
    handleFocusGroupShell,
    handleClickToDeleteGroupShell,
  } = useExpeditedProgramGroupShellCreator({
    positionPropsInfo,
    groupShellCount,
  });

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if (isFocused) {
        const target = e.target as HTMLElement;
        const clickSelectButton = selectButtonRef.current?.contains(target);

        if (clickSelectButton) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isFocused]);

  return (
    <S.GroupShellOutsideWrapper
      isFocused={isFocused}
      isClearGroupShell={isClearGroupShell}
      isDeleteGroupShell={isDeleteGroupShell}
      isAlertGroupShell={isAlertGroupShell}
    >
      {(isClearGroupShell || isDeleteGroupShell) && isEnableEdit && (
        <S.FixedDeleteButtonWrapper
          isShellTitle={!!getGroupShellTitle(articleSection, groupShellKey)}
        >
          <S.DeleteTableButtonWrapper>
            <S.DeleteTableButton onClick={handleClickToDeleteGroupShell}>
              <img src={IconXMark} alt="button for clear Row" />
              {focusStatus.type === "clear" ? `Clear` : null}
            </S.DeleteTableButton>
          </S.DeleteTableButtonWrapper>
        </S.FixedDeleteButtonWrapper>
      )}

      <S.GroupShellWrapper>
        <S.SelectWrapper>
          <S.GroupShellSelectButtonDividerWrapper
            ref={selectButtonRef}
            onClick={handleFocusGroupShell}
            isPossibleSelected
          >
            <S.GroupShellSelectButtonDivider className="select-button divider" />
            <S.GroupShellSelectButton className="select-button button">
              SELECT
            </S.GroupShellSelectButton>
          </S.GroupShellSelectButtonDividerWrapper>
          <S.GroupShell>
            {shellList.map(({ shellKey, subShellList }, shellListIndex) => (
              <React.Fragment key={`shellList-${String(shellListIndex)}`}>
                {newArrayToCountValue(
                  shellCount(shellKey, tableIndex, groupShellIndex),
                ).map((_, shellIndex) => (
                  <ExpeditedProgramSingleTypeShell
                    key={`shell-${String(groupTableIndex)}-${String(
                      tableIndex,
                    )}-${String(groupShellIndex)}-${String(shellIndex)}`}
                    groupTableKey={groupTableKey}
                    groupTableIndex={groupTableIndex}
                    groupShellKey={groupShellKey}
                    groupShellIndex={groupShellIndex}
                    shellKey={shellKey}
                    subShellList={subShellList}
                    shellIndex={shellIndex}
                    tableKey={tableKey}
                    tableIndex={tableIndex}
                    shellCount={shellCount(
                      shellKey,
                      tableIndex,
                      groupShellIndex,
                    )}
                    isShellOverOne={
                      shellCount(shellKey, tableIndex, groupShellIndex) > 1
                    }
                    isClearGroupShell={isClearGroupShell}
                    isDeleteGroupShell={isDeleteGroupShell}
                    isAlertGroupShell={isAlertGroupShell}
                  />
                ))}
              </React.Fragment>
            ))}
          </S.GroupShell>
        </S.SelectWrapper>
      </S.GroupShellWrapper>
    </S.GroupShellOutsideWrapper>
  );
};

export default ExpeditedProgramSingleTypeGroupShell;
